import {GameStateEnums} from '../../../../../shared/GameEnums'
import {UIGameMapTileView} from './UIGameMapTileView'
import {GameColor} from '../../GameColor'
import {UIGameManager} from '../UIGameManager'
import {UIGameAnimation, UIGameMechanic} from '../UIGameEventController'
import {ActionState, TileHexType, TurnState} from '@colonist/game-states'
import {TileHex} from '@colonist/game-mechanic-validators'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export class UIGameGoldTileController implements UIGameMechanic, UIGameAnimation {
    private readonly uiGameManager: UIGameManager

    private animate: boolean
    private tilesToAnimate: UIGameMapTileView[]
    private oldActionState: ActionState
    private animationTotalElapsedMS: number

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager

        this.animate = false
        this.tilesToAnimate = []
        this.oldActionState = ActionState.None

        this.uiGameManager.eventController.registerUIGameMechanic(this)
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.DiceStateUpdated: this.findGoldTilesToAnimate(); break
            case UIGameEventEnums.GameStateUpdated: this.checkIfGoldTileShouldBeFlashing(); break
            default: break
        }
    }

    onGameTick(deltaTime: number, elapsedMS: number) {
        this.animateGoldTile(deltaTime, elapsedMS)
    }

    private findGoldTilesToAnimate() {
        if(!this.uiGameManager.gameController.diceState.diceThrown) return
        this.resetTilesToAnimate()
        const totalDiceNumber = this.uiGameManager.gameController.diceState.dice1 + this.uiGameManager.gameController.diceState.dice2
        for(const tile of this.uiGameManager.gameState.mapState.tileState.tiles) {
            if(tile.tileType != TileHexType.Gold) continue
            if(tile.diceNumber != totalDiceNumber) continue
            if(!this.isPlayerTouchingGoldTile(tile)) continue

            const tileView = this.uiGameManager.mapController.getTileViewForHexFace(tile.hexFace)
            if(tileView == undefined) continue

            this.tilesToAnimate.push(tileView)
        }
        if(this.tilesToAnimate.length > 0) this.startAnimation()
    }

    private isPlayerTouchingGoldTile(tileHex: TileHex): boolean {
        for(const hexCorner of tileHex.hexFace.corners()) {
            const tileCorner = this.uiGameManager.gameState.mapState.tileState.getTileCorner(hexCorner)
            if(tileCorner == undefined) continue

            if(tileCorner.owner != undefined) return true
        }
        return false
    }

    private resetTilesToAnimate() {
        this.tilesToAnimate = []
    }

    private startAnimation() {
        this.animationTotalElapsedMS = 0
        this.uiGameManager.eventController.registerUIGameAnimation(this)
    }

    private endAnimation() {
        this.uiGameManager.eventController.unregisterUIGameAnimation(this)
        this.animate = false
        for(const view of this.tilesToAnimate) {
            view.resetHighlight()
        }
    }

    private checkIfGoldTileShouldBeFlashing() {
        if(this.shouldGoldAnimationEnd()) this.endAnimation()

        if(this.didFogTileGetRevealedAsGold()) return

        this.oldActionState = this.uiGameManager.gameController.currentState.actionState

        const arePlayerSelectingGoldResources = this.uiGameManager.gameController.currentState.actionState == ActionState.SelectCardsForPendingDistributions
        if(!arePlayerSelectingGoldResources) return

        this.animate = true
    }

    private shouldGoldAnimationEnd(): boolean {
        const wasGoldTileFlashing = this.oldActionState == ActionState.SelectCardsForPendingDistributions
        if(!wasGoldTileFlashing) return false

        const arePlayersStillSelectingGoldResources = this.uiGameManager.gameController.currentState.actionState == ActionState.SelectCardsForPendingDistributions
        if(arePlayersStillSelectingGoldResources) return false

        return true
    }

    private didFogTileGetRevealedAsGold(): boolean {
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.Turn) return true
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.SpecialBuildPhase) return true

        return false
    }

    private animateGoldTile(deltaTime: number, elapsedMS: number) {
        if(!this.animate) return

        this.animationTotalElapsedMS += elapsedMS
        const tintScaleAmplitude = 5
        const speedMultiplier = 3.2
        const meanTintScale = 5
        const progress = this.animationTotalElapsedMS / 1000
        const tintChange = Math.round(tintScaleAmplitude * Math.sin(progress * speedMultiplier) + meanTintScale)

        const tint = UIGameGoldTileController.getGrayScaleForCount(tintChange)
        for(const view of this.tilesToAnimate) {
            view.tileSpriteView.tint = tint
        }
    }

    static getGrayScaleForCount(tintChange: number): number {
        const mumberOfScales = tintChange % 12
        switch(mumberOfScales) {
            case 0: return GameColor.getColorHexValue(GameColor.Color.GrayScale13)
            case 1: return GameColor.getColorHexValue(GameColor.Color.GrayScale14)
            case 2: return GameColor.getColorHexValue(GameColor.Color.GrayScale15)
            case 3: return GameColor.getColorHexValue(GameColor.Color.GrayScale16)
            case 4: return GameColor.getColorHexValue(GameColor.Color.GrayScale17)
            case 5: return GameColor.getColorHexValue(GameColor.Color.GrayScale18)
            case 6: return GameColor.getColorHexValue(GameColor.Color.GrayScale19)
            case 7: return GameColor.getColorHexValue(GameColor.Color.GrayScale20)
            case 8: return GameColor.getColorHexValue(GameColor.Color.GrayScale21)
            case 9: return GameColor.getColorHexValue(GameColor.Color.GrayScale22)
            case 10: return GameColor.getColorHexValue(GameColor.Color.GrayScale23)
            case 11: return GameColor.getColorHexValue(GameColor.Color.GrayScale24)
            case 12: return GameColor.getColorHexValue(GameColor.Color.GrayScale25)
            default: return GameColor.getColorHexValue(GameColor.Color.GrayScale0)
        }
    }
}
